// Visual template editor — live WYSIWYG with debounced re-render via
// /api/admin/social/template_preview. Renders 3 variant tabs (Premium / Punchy
// / Minimal) and exposes the full pack of styling knobs.
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const VARIANTS = [
  { key: "premium", label: "Premium" },
  { key: "punchy", label: "Punchy" },
  { key: "minimal", label: "Minimal" },
];

const ACCENT_POS = [
  { v: "top", label: "Haut" },
  { v: "between", label: "Entre cover / texte" },
  { v: "bottom", label: "Bas" },
];

const WM_POS = [
  { v: "bottom-left", label: "Bas gauche" },
  { v: "bottom-center", label: "Centré" },
  { v: "bottom-right", label: "Bas droit" },
];

const LOGO_CORNERS = [
  { v: "none", label: "Pas de logo" },
  { v: "top-left", label: "Haut gauche" },
  { v: "top-right", label: "Haut droit" },
];

const ALIGN = [
  { v: "left", label: "Gauche" },
  { v: "center", label: "Centré" },
];

function Slider({ label, value, min, max, step = 1, onChange, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between text-[11px] text-[color:var(--gv-ink)]/70">
        <span>{label}</span>
        <span className="tabular-nums font-mono">{value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[color:var(--gv-pink)]"
        data-testid={testId}
      />
    </label>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-[11px] text-[color:var(--gv-ink)]/70 mb-1">{label}</div>
      <div className="flex gap-2 items-center">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-9 border border-[color:var(--gv-line)] rounded-[3px] cursor-pointer" data-testid={`${testId}-picker`} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm font-mono" data-testid={testId} />
      </div>
    </label>
  );
}

function RadioPills({ label, options, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[11px] text-[color:var(--gv-ink)]/70 mb-1">{label}</div>
      <div className="inline-flex flex-wrap gap-1" data-testid={testId}>
        {options.map((o) => (
          <button
            key={o.v} type="button"
            onClick={() => onChange(o.v)}
            className={`px-2.5 py-1 rounded-[3px] text-[12px] border transition ${value === o.v ? "bg-[color:var(--gv-ink)] text-white border-[color:var(--gv-ink)]" : "bg-white text-[color:var(--gv-ink)]/70 border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]"}`}
            data-testid={`${testId}-${o.v}`}
          >{o.label}</button>
        ))}
      </div>
    </div>
  );
}

export default function TemplateEditor({ defaultCoverUrl = "" }) {
  const [variants, setVariants] = useState([]);
  const [active, setActive] = useState("premium");
  const [spec, setSpec] = useState(null);   // local edited spec for active variant
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [coverUrl, setCoverUrl] = useState(defaultCoverUrl || "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1200");
  const [dirty, setDirty] = useState(false);
  const debounceRef = useRef(null);

  const loadVariants = async () => {
    setBusy(true);
    try {
      const r = await adminApi.socialListTemplates();
      setVariants(r.variants || []);
      const found = (r.variants || []).find((v) => v.key === active) || r.variants?.[0];
      if (found) { setSpec(found); setActive(found.key); }
    } finally { setBusy(false); }
  };

  /* eslint-disable */
  useEffect(() => { loadVariants(); }, []);
  /* eslint-enable */

  // Switching variants — load that variant's spec
  /* eslint-disable */
  useEffect(() => {
    const found = variants.find((v) => v.key === active);
    if (found) setSpec(found);
    setDirty(false);
    setPreview(null);
  }, [active, variants]);

  // Debounced live preview
  useEffect(() => {
    if (!spec) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await adminApi.socialTemplatePreview({
          variant: active,
          spec_overrides: spec,
          cover_url: coverUrl,
        });
        setPreview(r);
      } catch (e) {
        // swallow — preview will retry on next edit
        console.warn("template preview failed", e);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [spec, coverUrl, active]);
  /* eslint-enable */

  const set = (key, val) => {
    setSpec((s) => ({ ...s, [key]: val }));
    setDirty(true);
  };

  const save = async () => {
    if (!spec || !dirty) return;
    setSaving(true);
    try {
      const saved = await adminApi.socialUpdateTemplate(active, spec);
      setVariants((vs) => vs.map((v) => (v.key === active ? { ...v, ...saved } : v)));
      setDirty(false);
    } catch (e) { alert(`Erreur sauvegarde : ${e.message}`); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Réinitialiser la variante "${active}" aux défauts ?`)) return;
    setSaving(true);
    try {
      const r = await adminApi.socialResetTemplate(active);
      setVariants((vs) => vs.map((v) => (v.key === active ? { ...v, ...r } : v)));
      setSpec(r);
      setDirty(false);
    } finally { setSaving(false); }
  };

  const previewBlocks = useMemo(() => [
    { key: "square_fr", label: "Square FR 1:1", src: preview?.square_fr },
    { key: "story_fr", label: "Story FR 9:16", src: preview?.story_fr },
    { key: "square_en", label: "Square EN 1:1", src: preview?.square_en },
    { key: "story_en", label: "Story EN 9:16", src: preview?.story_en },
  ], [preview]);

  if (!spec) {
    return <div className="p-6 text-sm text-[color:var(--gv-ink)]/60" data-testid="template-editor-loading"><Loader2 className="inline animate-spin mr-2" size={14}/> Chargement des variantes…</div>;
  }

  return (
    <div data-testid="template-editor">
      {/* Variant tabs */}
      <div className="flex items-center gap-2 mb-4 border-b border-[color:var(--gv-line)]">
        {VARIANTS.map((v) => (
          <button
            key={v.key} type="button"
            onClick={() => setActive(v.key)}
            className={`px-4 py-2 text-sm font-semibold transition border-b-2 -mb-px ${active === v.key ? "border-[color:var(--gv-pink)] text-[color:var(--gv-pink)]" : "border-transparent text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-ink)]"}`}
            data-testid={`template-tab-${v.key}`}
          >
            {v.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={reset} disabled={saving} className="btn-secondary text-xs disabled:opacity-50" data-testid="template-reset">
            <RotateCcw size={12}/> Reset défauts
          </button>
          <button onClick={save} disabled={!dirty || saving} className="btn-primary text-xs disabled:opacity-50" data-testid="template-save">
            {saving ? <Loader2 className="animate-spin" size={12}/> : <Save size={12}/>} {dirty ? "Sauvegarder" : "Sauvegardé"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* LEFT — controls */}
        <div className="space-y-5">
          <div className="text-[12px] text-[color:var(--gv-ink)]/60">{spec.description}</div>

          {/* Couleurs */}
          <div className="grid grid-cols-2 gap-3">
            <ColorField label="Accent" value={spec.accent_color} onChange={(v) => set("accent_color", v)} testId="tpl-accent" />
            <ColorField label="Encre (titres)" value={spec.ink_color} onChange={(v) => set("ink_color", v)} testId="tpl-ink" />
            <ColorField label="Wordmark" value={spec.wordmark_color} onChange={(v) => set("wordmark_color", v)} testId="tpl-wm" />
            <ColorField label="Fond" value={spec.bg_color} onChange={(v) => set("bg_color", v)} testId="tpl-bg" />
          </div>

          {/* Barre d'accent */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <RadioPills label="Position barre d'accent" options={ACCENT_POS} value={spec.accent_position} onChange={(v) => set("accent_position", v)} testId="tpl-accent-pos" />
            <Slider label="Épaisseur barre (px)" value={spec.accent_thickness} min={1} max={20} onChange={(v) => set("accent_thickness", v)} testId="tpl-accent-thick" />
          </div>

          {/* Titre & label */}
          <div className="grid grid-cols-2 gap-3">
            <Slider label="Taille titre Square (px)" value={spec.title_size_square} min={36} max={80} onChange={(v) => set("title_size_square", v)} testId="tpl-title-sq" />
            <Slider label="Taille titre Story (px)" value={spec.title_size_story} min={48} max={96} onChange={(v) => set("title_size_story", v)} testId="tpl-title-st" />
            <Slider label="Taille label Square (px)" value={spec.label_size_square} min={18} max={40} onChange={(v) => set("label_size_square", v)} testId="tpl-label-sq" />
            <Slider label="Taille label Story (px)" value={spec.label_size_story} min={22} max={48} onChange={(v) => set("label_size_story", v)} testId="tpl-label-st" />
            <RadioPills label="Alignement titre (square)" options={ALIGN} value={spec.title_align} onChange={(v) => set("title_align", v)} testId="tpl-title-align" />
            <Slider label="Ratio cover Square" value={spec.cover_ratio_square} min={0.40} max={0.80} step={0.02} onChange={(v) => set("cover_ratio_square", v)} testId="tpl-cover-sq" />
          </div>

          {/* Wordmark */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <RadioPills label="Position wordmark (square)" options={WM_POS} value={spec.wordmark_position} onChange={(v) => set("wordmark_position", v)} testId="tpl-wm-pos" />
            <Slider label="Taille wordmark Square (px)" value={spec.wordmark_size_square} min={16} max={48} onChange={(v) => set("wordmark_size_square", v)} testId="tpl-wm-size" />
          </div>

          {/* Logo */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <RadioPills label="Logo geoVINUM (sticker)" options={LOGO_CORNERS} value={spec.show_logo ? spec.logo_corner : "none"} onChange={(v) => {
              if (v === "none") set("show_logo", false);
              else { set("show_logo", true); set("logo_corner", v); }
            }} testId="tpl-logo-pos" />
            <Slider label="Hauteur logo (px)" value={spec.logo_max_height} min={32} max={140} onChange={(v) => set("logo_max_height", v)} testId="tpl-logo-h" />
          </div>

          {/* CTA labels */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[11px] text-[color:var(--gv-ink)]/70 mb-1">CTA Story FR</div>
              <input value={spec.cta_label_fr || ""} onChange={(e) => set("cta_label_fr", e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid="tpl-cta-fr" />
            </label>
            <label className="block">
              <div className="text-[11px] text-[color:var(--gv-ink)]/70 mb-1">CTA Story EN</div>
              <input value={spec.cta_label_en || ""} onChange={(e) => set("cta_label_en", e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm" data-testid="tpl-cta-en" />
            </label>
          </div>

          {/* Cover test image */}
          <label className="block">
            <div className="text-[11px] text-[color:var(--gv-ink)]/70 mb-1">URL image de test (preview)</div>
            <input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-sm font-mono" data-testid="tpl-cover-url" />
          </label>
        </div>

        {/* RIGHT — live preview */}
        <div className="space-y-3" data-testid="template-preview-pane">
          <div className="text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60 font-semibold">Preview live · debounce 350ms</div>
          <div className="grid grid-cols-2 gap-3">
            {previewBlocks.map((b) => (
              <div key={b.key} className="border border-[color:var(--gv-line)] rounded-[4px] p-2 bg-[color:var(--gv-mist)]/30">
                <div className="text-[10px] text-[color:var(--gv-ink)]/50 mb-1 uppercase tracking-wide">{b.label}</div>
                {b.src ? (
                  <img src={b.src} alt={b.label} className="w-full rounded-[3px] border border-[color:var(--gv-line)]" data-testid={`tpl-preview-${b.key}`} />
                ) : (
                  <div className="aspect-square bg-white flex items-center justify-center text-xs text-[color:var(--gv-ink)]/40">
                    <Loader2 className="animate-spin" size={14}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
